/**
 * useEndpointModals - Estado e handlers de modais para API Endpoints
 */
import { useState, useCallback } from 'react';
import { ApiEndpoint } from '../../../../types';
import { useApiEndpoints } from './useApiEndpoints';
import { notify } from '../../../../utils/toast';

export const useEndpointModals = () => {
    const endpointsHook = useApiEndpoints();

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [endpointToEdit, setEndpointToEdit] = useState<ApiEndpoint | undefined>();
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [endpointToDelete, setEndpointToDelete] = useState<ApiEndpoint | undefined>();
    const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
    const [isRegenerateTokenOpen, setIsRegenerateTokenOpen] = useState(false);
    const [endpointToRegenerate, setEndpointToRegenerate] = useState<ApiEndpoint | undefined>();

    const openCreate = useCallback(() => {
        setEndpointToEdit(undefined);
        setIsFormOpen(true);
    }, []);

    const openEdit = useCallback((endpoint: ApiEndpoint) => {
        setEndpointToEdit(endpoint);
        setIsFormOpen(true);
    }, []);

    const openDelete = useCallback((endpoint: ApiEndpoint) => {
        setEndpointToDelete(endpoint);
        setIsDeleteOpen(true);
    }, []);

    const openRegenerateToken = useCallback((endpoint: ApiEndpoint) => {
        setEndpointToRegenerate(endpoint);
        setIsRegenerateTokenOpen(true);
    }, []);

    const save = useCallback(async (
        data: Omit<ApiEndpoint, 'id' | 'createdAt' | 'updatedAt' | 'requestCount' | 'lastUsedAt'>
    ) => {
        try {
            if (endpointToEdit) {
                await endpointsHook.updateEndpoint(endpointToEdit.id, data);
                notify.success('Endpoint atualizado!');
            } else {
                await endpointsHook.createEndpoint(data);
                notify.success('Endpoint criado!');
            }
            setIsFormOpen(false);
        } catch {
            notify.error('Erro ao salvar endpoint');
        }
    }, [endpointToEdit, endpointsHook]);

    const confirmDelete = useCallback(async () => {
        if (!endpointToDelete) return;
        try {
            await endpointsHook.deleteEndpoint(endpointToDelete.id);
            notify.success('Endpoint excluído!');
            setIsDeleteOpen(false);
            setEndpointToDelete(undefined);
        } catch {
            notify.error('Erro ao excluir endpoint');
        }
    }, [endpointToDelete, endpointsHook]);

    const bulkDelete = useCallback(async () => {
        const ids = Array.from(endpointsHook.selectedIds);
        try {
            await endpointsHook.deleteBulk(ids);
            notify.success(`${ids.length} endpoint(s) excluído(s)!`);
            setIsBulkDeleteOpen(false);
        } catch {
            notify.error('Erro ao excluir endpoints');
        }
    }, [endpointsHook]);

    const confirmRegenerateToken = useCallback(async () => {
        if (!endpointToRegenerate) return;
        try {
            await endpointsHook.regenerateToken(endpointToRegenerate.id);
            notify.success('Token regenerado! Copie o novo token.');
            setIsRegenerateTokenOpen(false);
            setEndpointToRegenerate(undefined);
        } catch {
            notify.error('Erro ao regenerar token');
        }
    }, [endpointToRegenerate, endpointsHook]);

    return {
        // Data
        endpoints: endpointsHook.endpoints,
        loading: endpointsHook.loading,
        selectedIds: endpointsHook.selectedIds,
        toggleSelection: endpointsHook.toggleSelection,
        toggleAllSelection: endpointsHook.toggleAllSelection,
        clearSelection: endpointsHook.clearSelection,
        refetch: endpointsHook.refetch,
        getEndpointUrl: endpointsHook.getEndpointUrl,
        generateToken: endpointsHook.generateToken,
        generateSlug: endpointsHook.generateSlug,

        // Modal state
        isFormOpen,
        setIsFormOpen,
        endpointToEdit,
        isDeleteOpen,
        setIsDeleteOpen,
        endpointToDelete,
        isBulkDeleteOpen,
        setIsBulkDeleteOpen,
        isRegenerateTokenOpen,
        setIsRegenerateTokenOpen,
        endpointToRegenerate,

        // Handlers
        openCreate,
        openEdit,
        openDelete,
        openRegenerateToken,
        save,
        confirmDelete,
        bulkDelete,
        confirmRegenerateToken,
    };
};
